import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@heidi/design-system/dialog';
import { Skeleton } from '@heidi/design-system/skeleton';
import { TypographyP2 } from '@heidi/design-system/typography';
import type { QueryPatientDocument } from '@heidi/patients/contracts';
import { Loader2 } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { type PatientProfileHost } from '../host';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientProfileId: string;
  document: QueryPatientDocument | null;
  host: PatientProfileHost;
}

const isPdf = (mimeType: string): boolean => mimeType === 'application/pdf';
const isImage = (mimeType: string): boolean => mimeType.startsWith('image/');

const DEFAULT_PAGE_WIDTH = 800;
const MIN_PAGE_WIDTH = 320;
const MAX_PAGE_WIDTH = 900;

export const PatientDocumentPreviewDialog = ({
  open,
  onOpenChange,
  patientProfileId,
  document,
  host,
}: Props) => {
  const intl = useIntl();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const { ReactPdfDocument: Document, ReactPdfPage: Page } = host;

  const { data, isLoading, isError } = host.useQueryPatientDocumentDownloadUrl(
    patientProfileId,
    document?.id,
    { enabled: open && !!document?.id }
  );

  const [numPages, setNumPages] = useState(0);
  const [docError, setDocError] = useState(false);

  const mime = document?.mime_type ?? '';
  const fileName = document?.file_name ?? '';
  const downloadUrl = data?.download_url;

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0) setContainerWidth(rect.width);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setNumPages(0);
      setDocError(false);
    }
  }, [open]);

  const pageWidth = containerWidth
    ? Math.max(MIN_PAGE_WIDTH, Math.min(containerWidth - 32, MAX_PAGE_WIDTH))
    : DEFAULT_PAGE_WIDTH;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[85dvh] max-w-4xl flex-col gap-3 overflow-hidden p-0">
        <DialogHeader className="shrink-0 border-b border-border px-5 pb-3 pt-5">
          <DialogTitle className="truncate">{fileName || '—'}</DialogTitle>
          <DialogDescription className="sr-only">
            {intl.formatMessage({
              defaultMessage: 'Document preview',
              id: '21ocDHW4B8',
            })}
          </DialogDescription>
        </DialogHeader>

        <div
          ref={containerRef}
          className="scrollbar-subtle flex min-h-0 flex-1 flex-col overflow-y-auto bg-surface-page"
        >
          {isLoading && (
            <div className="flex h-full flex-col gap-3 p-5">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="flex-1" />
            </div>
          )}

          {isError && (
            <div className="flex h-full items-center justify-center p-5">
              <TypographyP2 className="text-text-secondary">
                {intl.formatMessage({
                  defaultMessage: "Couldn't load preview. Please try again.",
                  id: '++CbQPI3jh',
                })}
              </TypographyP2>
            </div>
          )}

          {!isLoading && !isError && downloadUrl && (
            <>
              {isPdf(mime) && (
                <div className="flex flex-col items-center gap-3 py-4">
                  {docError ? (
                    <TypographyP2 className="text-text-secondary">
                      {intl.formatMessage({
                        defaultMessage: "Couldn't render this PDF.",
                        id: 'VhUG/ibdiK',
                      })}
                    </TypographyP2>
                  ) : (
                    <Document
                      file={downloadUrl}
                      onLoadSuccess={({ numPages: n }: { numPages: number }) => {
                        setNumPages(n);
                      }}
                      onLoadError={() => {
                        setDocError(true);
                      }}
                      loading={
                        <div className="flex flex-col items-center gap-3 py-8">
                          <Loader2 className="size-6 animate-spin text-text-tertiary motion-reduce:animate-none" />
                          <TypographyP2 className="text-text-secondary">
                            {intl.formatMessage({
                              defaultMessage: 'Loading preview',
                              id: 'wNxjEkxVDB',
                            })}
                          </TypographyP2>
                        </div>
                      }
                    >
                      {Array.from({ length: numPages }, (_, i) => (
                        <Page
                          key={i}
                          pageNumber={i + 1}
                          width={pageWidth}
                          className="mb-3 shadow-sm"
                          renderAnnotationLayer={false}
                          renderTextLayer={false}
                        />
                      ))}
                    </Document>
                  )}
                </div>
              )}

              {isImage(mime) && (
                <div className="flex h-full items-center justify-center overflow-auto p-5">
                  {docError ? (
                    <TypographyP2 className="text-text-secondary">
                      {intl.formatMessage({
                        defaultMessage: "Couldn't load this image.",
                        id: 'mk33dttAr8',
                      })}
                    </TypographyP2>
                  ) : (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={downloadUrl}
                      alt={fileName}
                      className="max-h-full max-w-full object-contain"
                      onError={() => setDocError(true)}
                    />
                  )}
                </div>
              )}

              {!isPdf(mime) && !isImage(mime) && (
                <div className="flex h-full flex-col items-center justify-center gap-3 p-5 text-center">
                  <TypographyP2 className="text-text-secondary">
                    {intl.formatMessage({
                      defaultMessage:
                        "Inline preview isn't available for this file type yet.",
                      id: 'hrtAGbJnXy',
                    })}
                  </TypographyP2>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
